import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetRoleQuery } from "@generated";
import { RoleForm } from "../RoleForm/RoleForm";
import { useRoleMutations, usePrivilegeDefinitions } from "../api";
import { emptyRoleForm } from "../types";
import type { RoleFormValues } from "../types";

const FORM_ID = "role-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useRoleMutations();
  const { data, isLoading, refetch } = useV2GetRoleQuery(
    { id: Number(id) },
    { refetchOnMountOrArgChange: true }
  );
  const { grouped, isLoading: defsLoading } = usePrivilegeDefinitions();
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<RoleFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<RoleFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyRoleForm;
    // The v2 API returns `privileges` as a hash { "scope.resource": level }
    // (the raw jsonb column) — NOT the array the generated swagger type claims.
    const privileges = {
      ...((attrs.privileges ?? {}) as unknown as Record<string, string>),
    };
    return {
      name: attrs.name ?? "",
      default: attrs.default ?? false,
      privileges,
    };
  }, [data]);

  if (isLoading || defsLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: RoleFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update role", err);
      setToast({ variant: "danger", text: "Could not save role." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Role"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <RoleForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          definitions={grouped}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
